<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div class="table-filtrable-wrapper">
    <el-table
      :header-cell-class-name="headerCellClass"
      :data="data"
      header-row-class-name="draggable"
      :empty-text="$t('main.orders.noData')"
      stripe
      v-loading="loading"
      element-loading-text="Cargando órdenes…"
      element-loading-background="rgba(240, 244, 248, 0.85)"
      ref="filtrableTable"
      class="orders-table"
    >
      <el-table-column
        v-for="column in columns"
        :label="column.label"
        :prop="column.field"
        :key="column.field"
        :sort-by="column.field"
        :min-width="column.type === 'Date' ? 120 : 80"
        align="center"
        sortable
        scope="col"
      >
        <template slot="header" slot-scope="scope">
          <el-date-picker
            v-if="column.type === 'Date'"
            v-model="filters[column.field]"
            type="datetimerange"
            range-separator=""
            clearable
            :default-time="['00:00:00', '23:59:59']"
            :picker-options="{ disabledDate: disableFutureDates }"
            :start-placeholder="column.label"
            @change="onDateRangeInput(column.field, $event)"
            @click.native.stop
          >
          </el-date-picker>
          <el-select
            v-else
            v-model="filters[column.field]"
            multiple
            filterable
            clear-on-select="true"
            allow-create
            reserve-keyword
            default-first-option
            :filter-method="(term) => onSearchTermChange(term, column.field)"
            popper-append-to-body
            popper-class="no-suggestions-dropdown"
            :placeholder="`${column.label}`"
            :key="column.field"
            @change="notifyFiltersChanged"
            @keyup.native.enter.prevent="onSelectEnter(column.field)"
            @visible-change="(open) => onDropdownVisible(open, column.field)"
            :no-data-text="
              !filters[column.field]
                ? 'Escribe para buscar…'
                : 'Escribe para buscar…'
            "
            no-match-text="No hay datos"
          >
            <el-option
              v-for="item in visibleOptions(column.field)"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </template>

        <template slot-scope="scope">
          <span v-if="column.type === 'Date'">
            {{ scope.row[column.field] | formatHour }}
          </span>
          <span v-else-if="Array.isArray(scope.row[column.field])">
            <div
              v-for="(itemData, itemIndex) in scope.row[column.field]"
              :key="itemData + itemIndex"
            >
              {{ itemData }}
              <hr v-if="itemIndex !== scope.row[column.field].length - 1" />
            </div>
          </span>
          <component
            v-else-if="column.type === 'Component'"
            :is="component"
            :order="scope.row"
          ></component>
          <span v-else>
            {{ scope.row[column.field] }}
          </span>
        </template>
      </el-table-column>

      <!-- Videos Disponibles -->
      <el-table-column :min-width="70" align="center">
        <template #header>Videos<br />Disponibles</template>
        <template slot-scope="scope">
          <div v-if="scope.row.status === 6" class="status-lost">
            <i class="el-icon-warning" style="margin-right: 4px"></i>
            <span>LOST</span>
          </div>
          <div
            v-else-if="scope.row.videoUrl === null"
            class="status-processing-badge"
          >
            <span class="status-processing-badge__dot"></span>
            <span>Procesando</span>
          </div>
          <div
            v-else-if="
              integrityData[scope.$index] !== undefined &&
              integrityData[scope.$index] !== null
            "
          >
            <integrity-pie
              :percentage="integrityData[scope.$index]"
              :key="'integrity-' + scope.$index"
            />
          </div>
          <div v-else>
            <span class="status-empty">—</span>
          </div>
        </template>
      </el-table-column>

      <!-- Acciones -->
      <el-table-column label="" prop="order" align="center" :min-width="90">
        <template slot="header">
          <div v-if="isAdmin" class="not-draggable-cell header-actions">
            <button class="btn-nueva-orden" @click="onAddOrder">
              <i class="el-icon-plus"></i> Nueva orden
            </button>
          </div>
        </template>
        <template slot-scope="scope">
          <div class="row-actions">
            <button
              v-if="scope.row.videoUrl !== null"
              class="row-action-btn row-action-btn--play"
              title="Ver vídeo"
              @click="handleRowAction('view', scope.row)"
            >
              <i class="fas fa-play"></i>
            </button>
            <div
              v-else
              class="row-action-btn row-action-btn--placeholder"
            ></div>
            <button
              class="row-action-btn row-action-btn--view"
              title="Ver datos"
              @click="handleRowAction('edit', scope.row)"
            >
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import Sortable from "sortablejs";
import { mapState } from "vuex";
import ViewOrderButton from "./ViewOrderButton";
import Create from "./Create.vue";
import IntegrityPie from "./IntegrityPie.vue";
import storage from "../utils/storage";

export default {
  name: "TableFiltrable",
  components: {
    "view-order-button": ViewOrderButton,
    Create,
    IntegrityPie,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    posibleFilters: {
      type: Object,
      default: () => ({}),
    },
    fields: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Object,
      required: true,
    },
    integrityData: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filters: {
        videoStart: [
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          new Date(Date.now()),
        ],
      },
      orders: [],
      searchQueries: {},
      resizeTimeout: null,
      isMobile: window.innerWidth < 768,
    };
  },
  created() {
    const saved = storage.loadInfoFilter();
    if (saved) {
      this.filters = { ...this.filters, ...saved };
      if (saved.createdAtFrom) {
        this.filters.createdAt = [saved.createdAtFrom, saved.createdAtTo];
      }
    }
    this.$emit("filtersChange", this.filters);
  },
  mounted() {
    let el = document.getElementsByClassName("draggable")[0];
    Sortable.create(el, {
      animation: 150,
      filter: ".not-draggable-cell",
      onEnd: (evt) => {
        let newIndex = evt.newIndex;
        let oldIndex = evt.oldIndex;
        this.array_move(this.fields, oldIndex, newIndex);
        this.reload();
      },
    });

    window.addEventListener("resize", this.onWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onWindowResize);
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
  },
  computed: {
    ...mapState("config", ["columnTypes"]),
    refs() {
      return this.$refs;
    },
    isAdmin() {
      return sessionStorage.getItem("role") === "admin";
    },
    columns() {
      const mobileFields = ["videoStart", "orderNumber", "recorderName"];
      const activeFields = this.isMobile
        ? this.fields.filter((f) => mobileFields.includes(f))
        : this.fields;
      return activeFields.map((field) => {
        let type = this.columnTypes[field] || "String";
        if (this.data[0] && this.data[0][field]) {
          let data = this.data[0][field];
          if (data instanceof Date) {
            type = "Date";
          }
          if (Array.isArray(data)) {
            type = "Array";
          }
        }
        let column = {
          field,
          label: this.labels[field],
          fitrable: true,
          type,
        };
        return column;
      });
    },
  },
  methods: {
    onWindowResize() {
      this.isMobile = window.innerWidth < 768;
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        if (this.$refs.filtrableTable) {
          this.$refs.filtrableTable.doLayout();
        }
      }, 100);
    },
    disableFutureDates(date) {
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);
      return date > endOfToday;
    },
    handleRowAction(action, order) {
      if (action === "view") {
        this.onOrderOpen({ order });
      } else if (action === "edit") {
        this.$router.push({ path: `/order/${order.id}` });
      }
    },
    getActionOptions(order) {
      const options = [];
      if (order.videoUrl !== null) {
        options.push({ value: "view", label: "Ver video" });
      }
      options.push({ value: "edit", label: "Ver datos" });
      return options;
    },
    onSearchTermChange(term, field) {
      this.$set(
        this.searchQueries,
        field,
        term.toString().trim().toLowerCase()
      );
    },
    onDropdownVisible(open, field) {
      if (open) {
        this.$set(this.searchQueries, field, "");
      }
    },
    visibleOptions(field) {
      const all = this.posibleFilters[field] || [];
      const term = this.searchQueries[field] || "";
      if (term) {
        return all
          .filter((item) => item.toLowerCase().includes(term))
          .slice(0, 14);
      }
      return all.slice(0, 14);
    },
    onDateRangeChange(field, range) {
      if (!range || range.length !== 2) return;
      this.$set(this.filters, field, range);
      this.$emit("filtersChange", {
        [`${field}From`]: range[0].toISOString(),
        [`${field}To`]: range[1].toISOString(),
      });
    },
    onDateRangeInput(field, range) {
      if (!Array.isArray(range) || range.length !== 2) {
        this.$set(this.filters, `${field}From`, null);
        this.$set(this.filters, `${field}To`, null);
        this.$set(this.filters, field, []);
        this.$emit("filtersChange", { ...this.filters });
        return;
      }
      const [from, to] = range;
      this.$set(this.filters, `${field}From`, from.toISOString());
      this.$set(this.filters, `${field}To`, to.toISOString());
      this.$set(this.filters, field, [from, to]);
      this.$emit("filtersChange", { ...this.filters });
    },
    onAddOrder() {
      this.$router.push({ name: "OrderCreate" });
    },
    onFilterInput(searchTerm, field) {
      this.$set(this.filters, field, searchTerm);
      this.$emit("filtersChange", this.filters);
    },
    orderTable(sortOrder) {
      this.refs.filtrableTable.sort(sortOrder);
    },
    onSelectEnter(field) {
      const current = this.filters[field];
      if (Array.isArray(current)) return;
      if (!current) return;
      this.$set(this.filters, field, [current]);
      this.$emit("filtersChange", this.filters);
    },
    onFilterBlur(field) {
      const current = this.filters[field];
      if (current && !Array.isArray(current)) return;
      const query = this.$refs.filterSelect.currentQuery;
      if (query && !this.filters[field].includes(query)) {
        this.filters[field] = [...this.filters[field], query];
        this.$emit("filtersChange", this.filters);
      }
    },
    onOrderOpen({ order, videoStart }) {
      console.log("OPEN VIDEO", order);
      this.$router.push({
        name: "VideoPlayerView",
        params: { id: order.id, order, videoStart },
      });
    },
    videoIntegrityIcon(integrity) {
      const iconMapping = {
        ALL: "el-icon-sunny",
        PARTIAL: "el-icon-cloudy",
        LOST: "el-icon-lightning",
      };
      return iconMapping[integrity] ?? "el-icon-bell";
    },
    headerCellClass(data) {
      if (this.fields[data.columnIndex]) {
        return "draggable-cell";
      } else {
        return "not-draggable-cell";
      }
    },
    array_move(arr, oldIndex, newIndex) {
      if (newIndex >= arr.length) {
        var k = newIndex - arr.length + 1;
        while (k--) {
          arr.push(undefined);
        }
      }
      arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
      return arr;
    },
    reload() {
      this.$emit("reload");
    },
    notifyFiltersChanged() {
      this.$emit("filtersChange", this.filters);
      for (const [field, selected] of Object.entries(this.filters)) {
        const arr = Array.isArray(selected) ? selected : [selected];
        for (const val of arr) {
          if (val && !this.posibleFilters[field]?.includes(val)) {
            this.$set(this.posibleFilters, field, [
              ...(this.posibleFilters[field] || []),
              val,
            ]);
          }
        }
      }
    },
  },
};
</script>

<style scoped>
/* ── WRAPPER ───────────────────────────────────────────────────── */
.table-filtrable-wrapper {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);
  overflow: visible;
}

/* ── TABLA BASE ────────────────────────────────────────────────── */
:deep(.orders-table) {
  width: 100%;
}

/* ── OVERLAY DE CARGA (paginación / filtros) ─────────────────────── */
:deep(.orders-table .el-loading-spinner .path) {
  stroke: #0284c7;
}

:deep(.orders-table .el-loading-spinner .el-loading-text) {
  color: #0284c7;
  font-weight: 600;
  font-size: 13px;
}

:deep(.orders-table .caret-wrapper) {
  display: inline-flex;
}

:deep(.orders-table .el-table__header-wrapper) {
  position: sticky;
  top: 0;
  z-index: 10;
}

:deep(.orders-table th.el-table__cell) {
  background-color: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

:deep(.orders-table td.el-table__cell) {
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
  font-size: 13px;
  overflow: visible;
}

:deep(.orders-table td.el-table__cell .cell) {
  overflow: visible;
}

:deep(.orders-table .el-table__row:hover td) {
  background-color: #f8fafc !important;
}

:deep(.orders-table .el-table__row--striped td) {
  background-color: #fafbfc;
}

/* ── FIX: filas striped no deben cortar el badge ───────────────── */
:deep(.orders-table .el-table__row--striped td.el-table__cell) {
  overflow: visible;
}

:deep(.orders-table .el-table__row--striped td.el-table__cell .cell) {
  overflow: visible;
}

/* ── FILTROS EN CABECERA ───────────────────────────────────────── */
:deep(.no-suggestions-dropdown .el-select-dropdown) {
  display: none !important;
}

:deep(.orders-table .el-input__inner) {
  border-color: #e2e8f0;
  border-radius: 6px;
  font-size: 11px;
  color: #0f172a;
}

:deep(.orders-table th.el-table__cell .cell) {
  display: flex !important;
  align-items: center !important;
  padding-left: 5px !important;
  padding-right: 0 !important;
  box-sizing: border-box;
}

:deep(.orders-table th .el-select) {
  flex: 1;
  min-width: 0;
}

:deep(.orders-table th .el-date-editor) {
  flex: 1;
  min-width: 0;
}

:deep(.orders-table .el-input__inner:focus) {
  border-color: #0284c7;
}

:deep(.el-tag) {
  border-radius: 4px;
  font-size: 11px;
}

/* ── ESTADOS (Videos Disponibles) ─────────────────────────────── */
.status-lost {
  font-size: 12px;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.status-processing-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fef3c7;
  color: #b45309;
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-processing-badge__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f59e0b;
  flex-shrink: 0;
  animation: processingPulse 1.5s ease infinite;
}

@keyframes processingPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.status-empty {
  color: #cbd5e1;
}

/* ── BOTÓN NUEVA ORDEN ─────────────────────────────────────────── */
.header-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.btn-nueva-orden {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background-color: #0284c7;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(2, 132, 199, 0.35);
  transition: background 0.15s, box-shadow 0.15s;
  white-space: nowrap;
}

.btn-nueva-orden:hover {
  background-color: #0369a1;
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.45);
}

/* ── BOTONES DE ACCIÓN POR FILA ────────────────────────────────── */
.row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.row-action-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  touch-action: manipulation;
}

.row-action-btn:hover {
  border-color: #0284c7;
  color: #0284c7;
}

.row-action-btn--play {
  background-color: #0284c7;
  border-color: #0284c7;
  color: #fff;
  box-shadow: 0 2px 6px rgba(2, 132, 199, 0.35);
}

.row-action-btn--play:hover {
  background-color: #0369a1;
  border-color: #0369a1;
  color: #fff;
  box-shadow: 0 3px 10px rgba(2, 132, 199, 0.5);
}

.row-action-btn--placeholder {
  width: 30px;
  height: 30px;
  visibility: hidden;
  pointer-events: none;
}

/* ── RESPONSIVE: escritorio reducido (≤ 1280px) ────────────────── */
@media (max-width: 1280px) {
  :deep(.orders-table th.el-table__cell) {
    padding: 6px 4px !important;
    font-size: 10px !important;
  }

  :deep(.orders-table td.el-table__cell) {
    padding: 7px 4px !important;
    font-size: 11px !important;
  }

  :deep(.orders-table .el-input__inner) {
    font-size: 11px !important;
    padding: 0 6px !important;
    height: 26px !important;
  }

  :deep(.orders-table .el-input__icon) {
    line-height: 26px !important;
  }

  .btn-nueva-orden {
    padding: 4px 8px !important;
    font-size: 11px !important;
  }

  .row-action-btn {
    width: 24px !important;
    height: 24px !important;
    font-size: 10px !important;
  }

  .row-action-btn--placeholder {
    width: 24px !important;
    height: 24px !important;
  }

  .status-processing-badge {
    font-size: 10px !important;
    padding: 2px 6px !important;
  }
}

/* ── RESPONSIVE: tablet (≤ 768px) ─────────────────────────────── */
@media (max-width: 768px) {
  .table-filtrable-wrapper {
    border-radius: 8px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  :deep(.orders-table th.el-table__cell) {
    padding: 8px 4px !important;
    font-size: 10px !important;
  }

  :deep(.orders-table td.el-table__cell) {
    padding: 10px 4px !important;
    font-size: 12px !important;
  }

  :deep(.orders-table .el-input__inner) {
    height: 32px !important;
    font-size: 12px !important;
    padding: 0 6px !important;
  }

  :deep(.orders-table .el-input__icon) {
    line-height: 32px !important;
  }

  :deep(.el-tag) {
    font-size: 10px !important;
    padding: 0 4px !important;
    height: 18px !important;
    line-height: 18px !important;
  }

  .row-action-btn {
    width: 44px !important;
    height: 44px !important;
    font-size: 14px !important;
    border-radius: 10px !important;
  }

  .row-action-btn--placeholder {
    width: 44px !important;
    height: 44px !important;
  }

  .row-actions {
    gap: 8px;
  }

  .btn-nueva-orden {
    padding: 6px 10px !important;
    font-size: 11px !important;
  }

  .status-processing-badge {
    font-size: 10px !important;
    padding: 2px 6px !important;
  }

  .status-lost {
    font-size: 11px !important;
  }
}

/* ── RESPONSIVE: móvil (≤ 480px) ──────────────────────────────── */
@media (max-width: 480px) {
  .table-filtrable-wrapper {
    border-radius: 6px;
  }

  :deep(.orders-table th.el-table__cell) {
    padding: 6px 2px !important;
    font-size: 9px !important;
    letter-spacing: 0 !important;
  }

  :deep(.orders-table td.el-table__cell) {
    padding: 8px 2px !important;
    font-size: 11px !important;
  }

  :deep(.orders-table .el-input__inner) {
    height: 34px !important;
    font-size: 12px !important;
    padding: 0 4px !important;
  }

  :deep(.orders-table .el-input__icon) {
    line-height: 34px !important;
    width: 22px !important;
  }

  :deep(.orders-table .el-input__inner::placeholder) {
    font-size: 10px !important;
  }

  :deep(.el-tag) {
    font-size: 9px !important;
    padding: 0 3px !important;
    max-width: 60px !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .row-action-btn {
    width: 44px !important;
    height: 44px !important;
    font-size: 15px !important;
  }

  .row-action-btn--placeholder {
    width: 44px !important;
    height: 44px !important;
  }

  .btn-nueva-orden {
    padding: 0 !important;
    font-size: 0 !important;
    gap: 0 !important;
    border-radius: 50% !important;
    width: 36px !important;
    height: 36px !important;
    justify-content: center !important;
  }

  .btn-nueva-orden .el-icon-plus {
    font-size: 14px !important;
  }
}
</style>

<style>
@media (max-width: 767px) {
  .orders-table td .cell,
  .orders-table th .cell {
    font-size: 11px;
    padding: 0 4px;
    word-break: break-word;
    white-space: normal;
  }
}
</style>
